import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  titleVariations,
  sarcasticVariations,
  CURRENT_CLIENT,
  enableAnimation,
  RANDOM_CHARS,
  ANIMATION_STEPS,
  STEP_DURATION,
  VARIATION_DISPLAY_TIME,
  SARCASTIC_DISPLAY_TIME,
  MIN_ANIMATION_DELAY,
  MAX_ANIMATION_DELAY,
  FIRST_ANIMATION_MIN_DELAY,
  FIRST_ANIMATION_MAX_DELAY
} from '../constants';
import { MatrixClientBrand } from './client-brand';
import { TenantLogoImg } from './tenant-logo-img';
import type { AnimationTrigger } from '../types';
import { MARKETING_HOME_LINK_CLASS } from '@/landing/marketing-home-link';
import { cn } from '@/lib/utils';

interface AnimatedTitleProps {
  onAnimationTrigger?: AnimationTrigger;
  headerOnPrimary?: boolean;
  logoOnLightBackground?: boolean;
  logoClassName?: string;
  logoStyle?: import('react').CSSProperties;
  /**
   * Pre-auth surfaces: render as a single focusable link to `/` and do not use click-to-sarcastic
   * on the title surface (scheduled animations still run when enabled).
   */
  navigateHome?: boolean;
  /** Matrix/text brand size when no tenant logo image (default `lg` for headers) */
  matrixBrandSize?: 'lg' | 'xl' | '2xl' | '3xl';
}

const homeLinkClassName = MARKETING_HOME_LINK_CLASS;

export const AnimatedTitle = ({
  onAnimationTrigger,
  headerOnPrimary,
  logoOnLightBackground,
  logoClassName,
  logoStyle,
  navigateHome = false,
  matrixBrandSize = 'lg',
}: AnimatedTitleProps) => {
  const cc = CURRENT_CLIENT as { logoPath?: string; logoPathLight?: string; logoPathDark?: string; brand?: string; name?: string };
  const brandWordmark = cc.brand || cc.name || 'Home';
  const matrixMinWidth = matrixBrandSize === '3xl' ? 'min(100%, 280px)' : '200px';
  const hasLogo = Boolean(cc.logoPath || cc.logoPathLight || cc.logoPathDark);

  const [currentText, setCurrentText] = useState(brandWordmark);
  const [displayText, setDisplayText] = useState(brandWordmark);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const animateTextTransition = (targetText: string) => {
    console.log(`🎭 Starting animation: "${currentText}" → "${targetText}"`);
    setIsAnimating(true);
    
    const maxLength = Math.max(currentText.length, targetText.length);
    let currentStep = 0;
    
    const animate = () => {
      if (currentStep < ANIMATION_STEPS) {
        // Randomization phase - show random characters
        const randomizedText = Array.from({ length: maxLength }, (_, i) => {
          const progress = currentStep / ANIMATION_STEPS;
          const shouldSettle = Math.random() < progress;
          
          if (shouldSettle && i < targetText.length) {
            return targetText[i];
          } else if (i < currentText.length && Math.random() < 0.3) {
            return currentText[i];
          } else {
            return RANDOM_CHARS[Math.floor(Math.random() * RANDOM_CHARS.length)];
          }
        }).join('').slice(0, maxLength);
        
        setDisplayText(randomizedText);
        currentStep++;
        setTimeout(animate, STEP_DURATION);
      } else {
        // Final settle to target text
        setDisplayText(targetText);
        setCurrentText(targetText);
        setIsAnimating(false);
        console.log(`✅ Animation complete: "${targetText}"`);
      }
    };
    
    animate();
  };
  
  // Regular animation cycle
  const triggerRegularAnimation = useCallback(() => {
    // Pick a random variation (excluding current client name)
    const availableVariations = titleVariations.filter(text => text !== brandWordmark);
    const randomVariation = availableVariations[Math.floor(Math.random() * availableVariations.length)];
    
    console.log(`🎬 Triggering regular animation cycle`);
    
    // Animate to the variation
    setTimeout(() => {
      animateTextTransition(randomVariation);
    }, 100);
    
    // Return to current client name after showing variation
    setTimeout(() => {
      if (randomVariation !== brandWordmark) {
        animateTextTransition(brandWordmark);
      }
    }, VARIATION_DISPLAY_TIME);
  }, []); // Removed currentText dependency

  // Sarcastic animation (for clicks and AI responses)
  const triggerSarcasticAnimation = useCallback(() => {
    const pool = sarcasticVariations;
    const randomSarcastic = pool[Math.floor(Math.random() * pool.length)];
    
    console.log(`😈 Triggering sarcastic animation: "${randomSarcastic}"`);
    
    // Show sarcastic variation
    setTimeout(() => {
      animateTextTransition(randomSarcastic);
    }, 100);
    
    // Return to current client name after longer display
    setTimeout(() => {
      animateTextTransition(brandWordmark);
    }, SARCASTIC_DISPLAY_TIME);
  }, []);

  // Handle click event
  const handleTitleClick = () => {
    console.log('🖱️ Title clicked - triggering variation response');
    triggerSarcasticAnimation();
  };

  // Function to schedule next animation
  const scheduleNextAnimation = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    const randomDelay = MIN_ANIMATION_DELAY + Math.random() * (MAX_ANIMATION_DELAY - MIN_ANIMATION_DELAY);
    console.log(`⏰ Next animation scheduled in ${Math.round(randomDelay/1000)}s`);
    
    timeoutRef.current = setTimeout(() => {
      triggerRegularAnimation();
      scheduleNextAnimation(); // Schedule the next one
    }, randomDelay);
  }, [triggerRegularAnimation]);

  // Separate effect for exposing trigger function
  useEffect(() => {
    if (hasLogo || !enableAnimation) {
      if (onAnimationTrigger) {
        // Provide no-op function when animation is disabled
        onAnimationTrigger(() => {});
      }
      return;
    }
    
    if (onAnimationTrigger) {
      onAnimationTrigger(triggerSarcasticAnimation);
    }
  }, [hasLogo, onAnimationTrigger, triggerSarcasticAnimation]);

  // Main animation scheduling effect (runs only once)
  useEffect(() => {
    if (hasLogo || !enableAnimation) {
      return; // Skip animation scheduling when disabled
    }
    
    // First animation after random delay
    const firstDelay = FIRST_ANIMATION_MIN_DELAY + Math.random() * (FIRST_ANIMATION_MAX_DELAY - FIRST_ANIMATION_MIN_DELAY);
    console.log(`🚀 First animation will trigger after ${Math.round(firstDelay/1000)}s`);
    
    const firstTimeout = setTimeout(() => {
      console.log(`🚀 First animation trigger executing now`);
      triggerRegularAnimation();
      scheduleNextAnimation(); // Start the recurring schedule
    }, firstDelay);
    
    return () => {
      clearTimeout(firstTimeout);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [hasLogo, triggerRegularAnimation, scheduleNextAnimation]); // Include dependencies

  if (hasLogo) {
    const inner = (
      <TenantLogoImg
        className={logoClassName}
        style={logoStyle}
        logoOnLightBackground={logoOnLightBackground}
        decorative={navigateHome}
      />
    );
    if (!navigateHome) return inner;
    return (
      <Link to="/" aria-label="Home" className={homeLinkClassName}>
        {inner}
      </Link>
    );
  }

  // Render static title if animation is disabled
  if (!enableAnimation) {
    const staticInner = (
      <div
        className="text-xl font-semibold transition-all duration-300"
        style={{ minWidth: matrixMinWidth }}
        {...(navigateHome ? { 'aria-hidden': true } : {})}
      >
        <MatrixClientBrand
          text={brandWordmark}
          size={matrixBrandSize}
          headerOnPrimary={headerOnPrimary}
        />
      </div>
    );
    if (!navigateHome) return staticInner;
    return (
      <Link to="/" aria-label="Home" className={cn(homeLinkClassName)}>
        {staticInner}
      </Link>
    );
  }

  // Render animated title
  const animatedInner = (
    <div
      className={cn(
        'text-xl font-semibold transition-all duration-300',
        navigateHome ? 'cursor-default' : 'cursor-pointer hover:opacity-75',
        isAnimating ? 'opacity-90' : 'opacity-100'
      )}
      style={{ minWidth: matrixMinWidth }}
      {...(!navigateHome ? { onClick: handleTitleClick } : {})}
      {...(navigateHome ? { 'aria-hidden': true } : {})}
    >
      <MatrixClientBrand text={displayText} size={matrixBrandSize} headerOnPrimary={headerOnPrimary} />
    </div>
  );

  if (!navigateHome) return animatedInner;

  return (
    <Link to="/" aria-label="Home" className={cn(homeLinkClassName, 'cursor-pointer')}>
      {animatedInner}
    </Link>
  );
};
